import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom, useAtomValue } from 'jotai'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Callout, DataListItem, Input2FA, RowsSkeletonLoader } from '@node-space/ui-components'
import { TWO_FA_REQUIRED_LENGTH } from 'components/TwoFA/TwoFAField'
import { useCreateContactBankAccount } from 'hooks/contacts/useCreateContactBankAccount'
import { useContactMigrationBeneficiaryUpdate } from 'hooks/useContactMigrationBeneficiaryUpdate'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import {
  contactActionAtom,
  contactAtom,
  contactInstrumentActionAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { FormFieldLabels } from 'pages/Contacts/types/fiatInstruments'
import {
  getConfirmPayeeBeneficiaryParams,
  mapFormDataToBeneficiaryDetails,
} from 'pages/Contacts/utils/fiatInstruments'
import { ConfirmationOfPayeeCallout } from 'pages/Wallets/components/FiatSend/components/ConfirmationOfPayeeCallout'
import { useConfirmBeneficiaryCallout } from 'pages/Wallets/components/FiatSend/hooks/useConfirmBeneficiaryCallout'
import { ConfirmationOfPayeeCalloutTypes } from 'pages/Wallets/components/FiatSend/types'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { ContactActionIds } from 'types/atoms/atom.contacts'
import { Address, BeneficiaryDetailsCurrency, PaymentMethods } from 'types/beneficiaries'
import { ContactPaymentInstrument } from 'types/contacts'
import { ConfirmPayeeBeneficiaryParams } from 'types/payoutsV2'
import { getAddress } from 'utils/beneficiaries'
import { ContactsSheets } from '../ContactsSheets'
import { BankDetailsConfirmationCallout } from './BankDetailsConfirmationCallout'

type FiatInstrumentConfirmationProps = {
  formFieldLabels: FormFieldLabels
  formValues: ContactFiatInstrumentFormData
  isLoading: boolean
  form: UseFormReturn<ContactFiatInstrumentFormData>
}

export const FiatInstrumentConfirmation = ({
  formFieldLabels,
  formValues,
  isLoading,
  form,
}: FiatInstrumentConfirmationProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { twoFactorEnabled } = useTwoFAContext()

  const contact = useAtomValue(contactAtom)
  const [contactAction, setContactAction] = useAtom(contactActionAtom)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)

  const [isBankDetailsConfirmed, setIsBankDetailsConfirmed] = useState(false)
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)

  useEffect(() => {
    form?.setValue('twoFactorCode', '')
  }, [form])

  const { mutate: updateBeneficiaryAsMigrated } = useContactMigrationBeneficiaryUpdate()

  const {
    onSubmitBankAccount,
    isLoading: isSubmittingBankAccount,
    calloutError,
  } = useCreateContactBankAccount({
    contact,
    formValues,
    form,
    onSubmit2FASuccess: (paymentInstrument: ContactPaymentInstrument) => {
      if (contactAction?.meta?.contactBeneficiary) {
        updateBeneficiaryAsMigrated(
          {
            beneficiary: contactAction?.meta?.contactBeneficiary,
            instrumentId: paymentInstrument?.id,
          },
          {
            onSettled: () => {
              queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACT_INSTRUMENTS] })
              queryClient.invalidateQueries({ queryKey: [reactQueryName.BENEFICIARIES] })
              contactInstrumentAction?.onClose?.()
              setContactInstrumentAction(null)
            },
          }
        )
      } else {
        contactInstrumentAction?.onClose?.()
        setContactInstrumentAction(null)
      }
    },
  })

  const beneficiaryDetails: ConfirmPayeeBeneficiaryParams = useMemo(() => {
    const beneficiary = mapFormDataToBeneficiaryDetails(contact, formValues)
    return getConfirmPayeeBeneficiaryParams(beneficiary)
  }, [contact, formValues])

  const { confirmPayeeData, confirmationOfPayeeCallout, isConfirmingPayee, editBeneficiary } =
    useConfirmBeneficiaryCallout({
      beneficiaryDetails,
      isPayout: false,
      isContact: true,
    })

  useEffect(() => {
    if (formValues?.twoFactorCode?.length === TWO_FA_REQUIRED_LENGTH && twoFactorEnabled) {
      onSubmitBankAccount?.()
    }
  }, [formValues?.twoFactorCode, twoFactorEnabled])

  const isSWIFT = formValues?.paymentMethods?.includes(PaymentMethods.SWIFT)
  const isUsd = formValues?.currency === BeneficiaryDetailsCurrency.USD
  const isZar = formValues?.currency === BeneficiaryDetailsCurrency.ZAR
  const isAccountTypeRequired = isUsd || isZar

  const bankAddressData: Address = {
    addressLine1: formValues?.bankAddressLine1,
    addressLine2: formValues?.bankAddressLine2,
    city: formValues?.bankCity,
    postCode: formValues?.bankPostCode,
    region: formValues?.bankRegion,
    country: formValues?.bankCountryName || formValues?.bankCountryCode,
  }

  const intermediaryBankAddressData: Address = {
    addressLine1: formValues?.intermediaryBankAddressLine1,
    addressLine2: formValues?.intermediaryBankAddressLine2,
    city: formValues?.intermediaryBankCity,
    postCode: formValues?.intermediaryBankPostCode,
    region: formValues?.intermediaryBankRegion,
    country: formValues?.intermediaryBankCountryCode,
  }

  const beneficiaryAddressData: Address = {
    addressLine1: formValues?.beneficiaryAddressLine1,
    addressLine2: formValues?.beneficiaryAddressLine2,
    city: formValues?.beneficiaryCity,
    postCode: formValues?.beneficiaryPostCode,
    region: formValues?.beneficiaryRegion,
    country: formValues?.beneficiaryCountryName || formValues?.beneficiaryCountryCode,
  }

  const beneficiaryAddress = getAddress(beneficiaryAddressData)
  const bankAddress = getAddress(bankAddressData)
  const intermediaryBankAddress = getAddress(intermediaryBankAddressData)

  const handleBankDetailsConfirmationChange = (isChecked: boolean) => {
    setIsBankDetailsConfirmed(isChecked)
  }

  const handleOn2FAChange = (value: string) => {
    form.setValue('twoFactorCode', value)
  }

  const handleEditContact = useCallback(() => {
    setContactAction({
      ...contactAction,
      actionId: ContactActionIds.CONTACT,
      header: t('contacts.addContact'),
      onClose: () => {
        setIsContactSheetOpen(false)
        contactAction?.onClose?.()
      },
      meta: {
        keepUpdatedContact: true,
      },
    })
    setIsContactSheetOpen(true)
  }, [setContactAction])

  const isEur = formValues?.currency === BeneficiaryDetailsCurrency.EUR

  return isConfirmingPayee ? (
    <Box paddingT={24}>
      <RowsSkeletonLoader rows={6} />
    </Box>
  ) : (
    <Box width="full" paddingT={12}>
      {!!calloutError && (
        <Box paddingB={12}>
          <Callout variant="error">{calloutError}</Callout>
        </Box>
      )}
      {/* Beneficiary details */}
      <Box>
        <DataListItem
          size="large"
          id="currency"
          title={t('formFields.currency')}
          body={formValues?.currency}
          layout={'horizontal'}
        />
        <DataListItem
          size="large"
          id="description"
          title={t('formFields.beneficiaryDescription')}
          body={formValues?.description}
          layout={'horizontal'}
        />
        {beneficiaryAddress && (
          <DataListItem
            size="large"
            id="beneficiaryAddress"
            title={t('beneficiaryAddress')}
            body={beneficiaryAddress}
            layout={'horizontal'}
          />
        )}
      </Box>

      {/* Bank Details */}
      <Box>
        <DataListItem
          size="large"
          id="accountNumber"
          title={formFieldLabels?.accountNumber}
          body={formValues?.accountNumber}
          layout={'horizontal'}
        />
        <DataListItem
          size="large"
          id="bankCode"
          title={formFieldLabels?.bankCode}
          body={formValues?.bankCode}
          layout={'horizontal'}
        />
        {isAccountTypeRequired && formValues?.accountType && (
          <DataListItem
            size="large"
            id="accountType"
            title={t('formFields.accountType')}
            body={formValues?.accountType}
            layout={'horizontal'}
          />
        )}
        {formValues?.routingNumber && (
          <DataListItem
            size="large"
            id="routingNumber"
            title={t('formFields.routingNumber')}
            body={formValues?.routingNumber}
            layout={'horizontal'}
          />
        )}
        {formValues?.bankName && (
          <DataListItem
            size="large"
            id="bankCode"
            title={t('bankName')}
            body={formValues?.bankName}
            layout={'horizontal'}
          />
        )}
        {bankAddress && (
          <DataListItem
            size="large"
            id="bankAddress"
            title={t('bankAddress')}
            body={bankAddress}
            layout={'horizontal'}
          />
        )}
      </Box>
      {/* IntermediaryBankDetails */}
      {isSWIFT && formValues?.intermediaryBankCode && (
        <Box paddingT={20}>
          <h2 className="text-foreground-prominent text-h2">
            {t('manageBeneficiaries.intermediaryBankDetails')}
          </h2>
          <DataListItem
            size="large"
            id="intermediaryBankName"
            title={t('manageBeneficiaries.intermediarysBankName')}
            body={formValues?.intermediaryBankName}
            layout={'horizontal'}
          />
          <DataListItem
            size="large"
            id="intermediaryBankName"
            title={t('bicSwiftCode')}
            body={formValues?.intermediaryBankCode}
            layout={'horizontal'}
          />
          {formValues?.intermediaryBankRoutingNumber && (
            <DataListItem
              size="large"
              id="routingNumber"
              title={t('formFields.routingNumber')}
              body={formValues?.intermediaryBankRoutingNumber}
              layout={'horizontal'}
            />
          )}
          {intermediaryBankAddress && (
            <DataListItem
              size="large"
              id="bankAddress"
              title={t('intermediaryBankAddress')}
              body={intermediaryBankAddress}
              layout={'horizontal'}
            />
          )}
        </Box>
      )}

      <Box flexCol gap={12}>
        {confirmationOfPayeeCallout && (
          <ConfirmationOfPayeeCallout
            type={ConfirmationOfPayeeCalloutTypes.BANK_ACCOUNT}
            confirmationOfPayeeCallout={confirmationOfPayeeCallout}
            confirmPayeeData={confirmPayeeData}
            editBeneficiary={editBeneficiary}
            handleEditBeneficiary={handleEditContact}
          />
        )}
        {!isEur && (
          <BankDetailsConfirmationCallout
            onDetailsConfirmationChange={handleBankDetailsConfirmationChange}
          />
        )}
      </Box>

      <Box width="full" paddingB={24} paddingT={24}>
        <Input2FA
          error={form.formState?.errors?.twoFactorCode?.message}
          label={t('authCodeConfirm')}
          testId="add-fiat-instrument-2fa"
          disabled={isEur && !isBankDetailsConfirmed}
          loading={isLoading || isSubmittingBankAccount}
          onChange={handleOn2FAChange}
          value={formValues?.twoFactorCode}
        />
      </Box>
      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet />
    </Box>
  )
}
